import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppRoutes from './routes';
import GlobalStyles from './styles/GlobalStyles.js';
import { ThemeProvider } from './shared/contexts/ThemeContext';
import { ToastProvider } from './shared/contexts/ToastContext';
import { CartProvider } from './features/shop/contexts/CartContext';
import WhatsAppChat from 'features/common/WhatsAppChat';
import Navbar from './shared/components/Navbar/Navbar';
import CartDrawer from './features/shop/components/CartDrawer';
import './styles/App.css';

// Module scope: one client for the app's lifetime, safe under StrictMode.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      gcTime: 5 * 60_000,
    },
  },
});

// There used to be an unconditional 800ms full-screen overlay here. It gated
// nothing — no data was being awaited — and pushed Largest Contentful Paint back
// by 800ms on every single visit, which is a live ranking signal. Removed.
const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <GlobalStyles />
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            {/* CartProvider sits outside Router so Navbar's badge can read it */}
            <CartProvider>
              <Router>
                <Navbar />

                <AnimatePresence mode="wait">
                  <AppRoutes />
                </AnimatePresence>

                {/* Opens itself whenever a product is added to the cart */}
                <CartDrawer />

                {/* WhatsApp Chat Button */}
                <WhatsAppChat />
              </Router>
            </CartProvider>
          </ToastProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
